import { addEntity, createPlace, setRootEntity } from "@client/lib/layout";
import { upgradeEntities } from "@client/lib/upgrade";
import { type ContentAppState, type Entities, EntityType, type LocalContent } from "@client/types";
import { type Api, type InitResponseData } from "@client/types/Api";
import { type ComponentAppState } from "@client/types/AppState/component";
import { type LabelsAppState } from "@client/types/AppState/labels";
import { type LayoutAppState, type LayoutContentAppState } from "@client/types/AppState/layouts";
import { type PagePopupAppState, type PopupAppState } from "@client/types/AppState/popups";
import { type StyleSheetAppState } from "@client/types/AppState/styleSheet";
import { type Nullable } from "@common/declarations";
import { toMap } from "@common/lib/array";
import { type InitSuccessActionData } from "../actions";

const toLocal = (json: string | null, isDefaultCulture: boolean): LocalContent | null => {
  if (json) {
    // TODO: upgrade
    return JSON.parse(json);
  }

  return isDefaultCulture ? null : {};
};

const toEntities = (styleSheet: StyleSheetAppState, json: string | null, addMain: boolean): Entities => {
  let entities: Entities = {};
  if (json) {
    const content = JSON.parse(json);
    entities = upgradeEntities(styleSheet.data, "entities" in content ? content.entities : content);
    if (!entities.main) {
      const main = createPlace("main");
      entities = {
        ...addEntity({}, main),
        ...setRootEntity(entities, main)
      };
    }
  } else {
    entities = addMain ? addEntity({}, createPlace("main")) : {};
  }

  return entities;
};

const layoutContent = (
  styleSheet: StyleSheetAppState,
  content: Api.Content,
  isDefaultCulture: boolean
): LayoutContentAppState => ({
  local: toLocal(content.localJson, isDefaultCulture),
  settings: JSON.parse(content.json)
});
const mappers = {
  init: (data: InitResponseData, isDefaultCulture: boolean): InitSuccessActionData => {
    const styleSheet = mappers.styleSheet(data.styleSheet, isDefaultCulture);
    return {
      components: mappers.components(styleSheet, data.components, isDefaultCulture),
      content: mappers.content(styleSheet, data.content, true, isDefaultCulture),
      popups: mappers.popups(styleSheet, data.popups, isDefaultCulture),
      pagePopups: mappers.pagePopups(styleSheet, data.pagePopups, isDefaultCulture),
      events: data.events,
      funnelInfo: data.funnelInfo,
      layoutContent: mappers.layoutContent(styleSheet, data.layoutContent, isDefaultCulture),
      page: data.page,
      pages: data.pages,
      portal: data.portal,
      redirectUrl: data.redirectUrl,
      styleSheet
    };
  },
  components: (
    styleSheet: StyleSheetAppState,
    components: Api.Component[],
    isDefaultCulture: boolean
  ): ComponentAppState[] =>
    components.map((x) => ({
      id: x.id,
      contentId: x.contentId,
      name: x.name,
      entities: JSON.parse(x.content.json),
      local: x.content.localJson ? JSON.parse(x.content.localJson) : isDefaultCulture ? null : {},
      isDeleted: x.isDeleted
    })),
  content: (
    styleSheet: StyleSheetAppState,
    content: Nullable<Api.Content>,
    addPlace: boolean,
    isDefaultCulture: boolean
  ): ContentAppState => ({
    entities: toEntities(styleSheet, content.json, addPlace),
    local: toLocal(content.localJson, isDefaultCulture)
  }),
  labels: (labels: Api.Labels[]): LabelsAppState =>
    toMap(
      labels,
      (x) => x.category,
      (x) => x.i18n
    ),
  layoutContent: (
    styleSheet: StyleSheetAppState,
    content: Api.Content | null,
    isDefaultCulture: boolean
  ): LayoutContentAppState | null => (content ? layoutContent(styleSheet, content, isDefaultCulture) : null),
  layouts: (styleSheet: StyleSheetAppState, layouts: Api.Layout[], isDefaultCulture: boolean): LayoutAppState[] =>
    layouts.map((x) => ({
      id: x.id,
      name: x.name,
      ...layoutContent(styleSheet, x.content, isDefaultCulture)
    })),
  pagePopups: (
    styleSheet: StyleSheetAppState,
    items: Api.PagePopup[],
    isDefaultCulture: boolean
  ): PagePopupAppState[] =>
    items.map((x) => ({
      local: x.localContentJson ? JSON.parse(x.localContentJson) : isDefaultCulture ? null : {},
      id: x.id,
      settings: JSON.parse(x.contentJson)
    })),
  popups: (styleSheet: StyleSheetAppState, popups: Api.Popup[], isDefaultCulture: boolean): PopupAppState[] =>
    popups.map(({ content, ...x }) => {
      const entities = JSON.parse(content.json);
      return {
        ...x,
        entities,
        entityId: Object.keys(entities).find((id) => entities[id].type === EntityType.Popup)!,
        local: content.localJson ? JSON.parse(content.localJson) : isDefaultCulture ? null : {},
        state: "closed"
      };
    }),
  styleSheet: (styleSheet: Api.StyleSheet, isDefaultCulture: boolean): StyleSheetAppState => ({
    id: styleSheet.id,
    data: JSON.parse(styleSheet.content.json),
    local: styleSheet.content.localJson ? JSON.parse(styleSheet.content.localJson) : isDefaultCulture ? null : {}
  })
};

export default mappers;
